// src/components/mitchRoom/UsernameEditor.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { editNiceUsername } from '../../services/api';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';

/**
 * Inline editor for the admin's public display name (nice_username).
 * Single-admin tool, so the field is always editable (no display/edit toggle);
 * Save is only enabled when the name has actually changed. Enter submits.
 */
export default function UsernameEditor({ user, initialUsername, onUsernameUpdate }) {
  const [tempName, setTempName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTempName(initialUsername || '');
  }, [initialUsername]);

  const isDirty = tempName.trim() !== (initialUsername || '') && tempName.trim() !== '';

  const handleSubmit = () => {
    if (!isDirty || saving) return;
    setSaving(true);
    editNiceUsername(user.user.wp_id, tempName)
      .then((res) => {
        onUsernameUpdate(res.nice_username);
        toast.success('Username updated');
      })
      .catch((err) => {
        toast.error(err?.message || 'Failed to update username');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="flex w-max max-w-full flex-wrap items-center gap-2 rounded-token-large border border-borderGrey bg-white p-2">
      <Label htmlFor="nice-username" className="shrink-0 text-token-md">
        Username
      </Label>
      <Input
        id="nice-username"
        value={tempName}
        onChange={(e) => setTempName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
        }}
        placeholder="Enter username"
        className="w-[200px]"
      />
      <Button size="sm" onClick={handleSubmit} disabled={!isDirty || saving}>
        {saving && <Loader2 className="animate-spin" />}
        Save
      </Button>
    </div>
  );
}

UsernameEditor.propTypes = {
  user: PropTypes.object.isRequired,
  initialUsername: PropTypes.string,
  onUsernameUpdate: PropTypes.func.isRequired,
};
